import React from 'react'
import { useI18n } from '../../i18n/I18nContext'

/**
 * Shared, presentation-only UI state primitives.
 *
 * These intentionally carry no business logic, data fetching, or permission
 * awareness — they only render markup using the global design tokens defined in
 * styles/globals.css (--surface, --text, --primary, --brand-*, etc.). Keep them
 * free of side effects so they can be reused by any workspace and by the
 * ErrorBoundary fallback.
 */

export const LoadingState: React.FC<{ label?: string; testId?: string }> = ({ label, testId }) => {
  const { t } = useI18n()
  return <div className="ui-state ui-state--loading" role="status" aria-live="polite" data-testid={testId}>
    <span className="ui-state__spinner" aria-hidden="true" />
    <span>{label || t('Loading...')}</span>
  </div>
}

export const EmptyState: React.FC<{
  title: string
  description?: string
  action?: React.ReactNode
  testId?: string
}> = ({ title, description, action, testId }) => (
  <div className="ui-state ui-state--empty" data-testid={testId}>
    <strong>{title}</strong>
    {description ? <p>{description}</p> : null}
    {action ? <div className="ui-state__action">{action}</div> : null}
  </div>
)

export const ErrorState: React.FC<{
  title?: string
  message?: string
  onRetry?: () => void
  retryLabel?: string
  /** Pre-formatted diagnostic text. Caller decides whether it is safe to show. */
  details?: string
  testId?: string
}> = ({
  title,
  message,
  onRetry,
  retryLabel,
  details,
  testId,
}) => {
  const { t } = useI18n()
  const resolvedTitle = t(title === undefined ? 'Something went wrong' : title)
  const resolvedMessage = t(message === undefined ? 'This section could not be displayed.' : message)
  return <div className="ui-state ui-state--error" role="alert" data-testid={testId}>
    <strong>{resolvedTitle}</strong>
    {resolvedMessage ? <p>{resolvedMessage}</p> : null}
    {onRetry ? (
      <div className="ui-state__action">
        <button type="button" className="btn btn-primary" onClick={onRetry}>{t(retryLabel || 'Retry')}</button>
      </div>
    ) : null}
    {details ? (
      <details className="ui-state__details">
        <summary>{t('Technical details')}</summary>
        <pre>{details}</pre>
      </details>
    ) : null}
  </div>
}
